import React, { FC, useState, useEffect } from 'react'
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
import { navigate } from "@reach/router"
// Components
import { WithTip } from 'components/tooltip/with-tip'
import { alphanum } from "components/table/lib/sort"
// Styles
import { FolderPath_s, HeaderIcon_s, HomeIcon_s, PropertySelection_s, CalcStatus_s } from 'components/wrapper/header/header-styles'
import { Dropdown_s } from 'styles/input-styles'
import { ShortcutHint_s } from 'styles/shortcut-styles'
// Icons
import { HomeIcon } from 'images/icons/header/'
import { CaretLeftIcon, CaretRightIcon } from 'images/icons/general/'
import { CommercialIcon, HospitalityIcon, MixedUseIcon, MFRentIcon } from 'images/icons/sidebar'
// Data / Streams
import { CalcStatus } from "data/interface/calc"
import folderService from 'data/folder'
import propertyService from 'data/property'
import keyService, * as chord from "data/key"
import { local, setlocal, clearlocal } from 'data/lib/local-json'
import toggles from 'feature-toggles'

const PropertySelection: FC = () => {
  const [properties, updateProperties] = useState<object[]>([]);
  const [current, updateCurrent] = useState<number|null>(null)
  const [folders, updateFolders]       = useState<object[]>([]);
  const [property, updateProperty]     = useState<object|null>({propertyid: -1});
  const [hintsVisible, setHintsVisible] = useState<boolean>(false)

  if (current !== null){
    const p = _.find(properties, { propertyid: current });
    if (!_.isEqual(p, property)) updateProperty(p)
  }

  useEffect(() => {
    const folderSubscription = folderService.stream().subscribe(data => {
      updateFolders(data.value);
    });

    const propertySubscription = propertyService.stream().subscribe(data => {
      if (data.state !== 'ok') return
      const properties = [...data.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code")))
      updateProperties(_.filter(properties, (p) => p.folderid === _.last(local('folderPath'))));
      updateCurrent(data.current)
      if(!_.isEqual(data.current, property)) {
        if(data.current) updateProperty({propertyid: data.current})
        else updateProperty(null)
      }
    });

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_ALT && k.down === true:
          setHintsVisible(true)
          break
        case k.chord === chord.CTRL_ALT && k.down === false:
          setHintsVisible(false)
          break
        case k.chord === chord.CTRL_ALT_E && k.down === true: // dropdown hotkey selection
          let ele = document.getElementById("header-property-selector-dropdown")
          if(ele && ele.click) ele.click()
          break
      }
    })

    folderService.fetchAllFolders()
    const folder = _.last(local('folderPath'))
    folder && propertyService.fetchByFolderId(folder, false)

    function handleFolderPathChange() {
      const folder = _.last(local('folderPath'))
      folder && propertyService.fetchByFolderId(folder, false)
    }

    window.addEventListener('set-folder-path', handleFolderPathChange)

    return () => {
      folderSubscription.unsubscribe();
      propertySubscription.unsubscribe();
      keySubscription.unsubscribe()
      window.removeEventListener('set-folder-path', handleFolderPathChange)
    }
  }, []);

  function highlightSelected(scroll=false) {
    setTimeout(() => {
      const scrollable = document.querySelector("#header-property-selector-dropdown .p-dropdown-items-wrapper ul")
      if(scrollable) {
        _.each(scrollable.children, (c) => c.classList.remove("hovering"))
        const currentSelected = _.find(scrollable.children, (c) => c.innerText === _.get(property, "code"))
        if(currentSelected) {
          if(scroll) currentSelected.scrollIntoView()
          currentSelected.classList.add("hovering")
        }
      }
    }, 309)
  }

  function getFolder(){
    return _.last(getFolderPath())
  }

  function getFolderPath() {
    const fs = _.map(local('folderPath'), folderid => {
      const folder = _.find(folders, {folderid})
      return _.pick(folder, 'folderid', 'code')
    })
    return fs
  }

  function onClickFolder(e: any, home=false):void {
    e.preventDefault();
    if (home) {
      clearlocal('folderPath')
    } else {
      const newFolderPath = getFolderPath().slice(0, -1).map(f => f.folderid);
      setlocal('folderPath', newFolderPath);
    }
    propertyService.clearSelectedProperty()
    if(toggles.displayFolderExplorer()) navigate("/n/folder/explorer")
    else window.location.href = '/folder'
  }

  function renderPiece(folder) {
    const trunc = s => s && s.length > 15 ? s.slice(0,15) + '...' : s
    const isCurrent = _.isEqual(folder, getFolder())

    return (
      <FolderPath_s key={folder.folderid}>
          <WithTip position="bottom">
            <div>{folder.code}</div>
            <a
              href="#"
              onClick={!isCurrent ? (e) => onClickFolder(e) : undefined}
              style={isCurrent ? { cursor: 'default', color: '#9DA8B2', textDecoration: 'none' } : { textDecoration: 'none' }}>
              {trunc(folder.code)}
            </a>
          </WithTip>
        {' '}
        &nbsp;
        <HeaderIcon_s><Isvg src={CaretRightIcon} className="caret-right" /></HeaderIcon_s>
      </FolderPath_s>
    )
  }

  const template = (option) => {
    const commercialIcon  = <Isvg key={CommercialIcon} src={CommercialIcon} cacheGetRequests width={18} />;
    const hospitalityIcon = <Isvg key={HospitalityIcon} src={HospitalityIcon} cacheGetRequests width={15} />;
    const mfIcon          = <Isvg key={MFRentIcon} src={MFRentIcon} cacheGetRequests width={12} />;
    const mixedUseIcon    = <Isvg key={MixedUseIcon} src={MixedUseIcon} cacheGetRequests width={18} />;

    const calcStatus  = option.calcdetail?.status || null;
    const errordetail = option.errordetail;
    const type        = option.type;

    return (
      <div>
        {calcStatus === null && errordetail === null && <CalcStatus_s />}
        {calcStatus == CalcStatus.CALCULATED && !errordetail && <CalcStatus_s status="Success" />}
        {calcStatus === CalcStatus.CALC_WITH_ERR || (!calcStatus && errordetail) && <CalcStatus_s status="Danger" />}
        {calcStatus === CalcStatus.WARNING || (calcStatus == CalcStatus.CALCULATED && errordetail) && <CalcStatus_s status="Warning" />}
        {calcStatus === CalcStatus.IN_PROCESS && <CalcStatus_s status="Loading" />}
        {type === "Commercial" && commercialIcon}
        {type === "Hospitality" && hospitalityIcon}
        {type === "Multifamily" && mfIcon}
        {type === "Mixed-Use" && mixedUseIcon}
        {option.code}
      </div>
    );
  }

  const folderPath = getFolderPath()
  const showEllipsis = folderPath.length > 2
  const path = showEllipsis ? _.map(folderPath, renderPiece).slice(-2) : _.map(folderPath, renderPiece)
  const ellided = <WithTip position="bottom">{_.map(folderPath.slice(0,-2), 'code').join('/')}...</WithTip>

  return (
    <PropertySelection_s>
      <WithTip position="bottom">
        Folder Explorer
        <HomeIcon_s onClick={(e)=>onClickFolder(e, true)}>
          <Isvg id="homeIcon" src={HomeIcon} />
        </HomeIcon_s>
        </WithTip>
      { showEllipsis && <HeaderIcon_s><Isvg src={CaretLeftIcon} /></HeaderIcon_s> }
      { showEllipsis && <HeaderIcon_s>{ellided}</HeaderIcon_s> }
      { showEllipsis ? <HeaderIcon_s><Isvg src={CaretLeftIcon} /></HeaderIcon_s> : <HeaderIcon_s><Isvg src={CaretRightIcon} className="caret-right" /></HeaderIcon_s> }
      { path }
      <div>
        <Dropdown_s
          id="header-property-selector-dropdown"
          value={property ? property['code'] : null}
          options={properties}
          optionLabel="code"
          itemTemplate={template}
          onChange={(e) => propertyService.selectProperty(e.value.propertyid)}
          filter={true}
          filterPlaceholder="Select a Property"
          filterBy="code"
          onFocus={(e) => highlightSelected(true)}
          placeholder={property ? property['code'] : 'Select a Property'}
        />
        { hintsVisible && <ShortcutHint_s className="header-hint" visible={hintsVisible}>E</ShortcutHint_s> }
      </div>
    </PropertySelection_s>
  )
}

export default PropertySelection;
